import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Radio, Play, ArrowLeft, ExternalLink, MapPin, Clapperboard } from "lucide-react";
import { api, fileUrl, thumbUrl } from "../lib/api";
import { Pagination } from "../components/Pagination";
import { useSeo, Reveal, SectionTitle } from "../components/VIP";
import { PipeText } from "../lib/pipeText";

export function SpotlightEmbed({ s, className = "" }) {
  const url = s.video_url || "";
  if (s.video_platform === "facebook" || url.includes("facebook.com")) {
    return (
      <div className={`relative aspect-video bg-black rounded-2xl overflow-hidden ${className}`}>
        <iframe title={s.title} src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=false`}
          className="absolute inset-0 w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture; web-share" allowFullScreen data-testid="spotlight-embed" />
      </div>
    );
  }
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) {
    return (
      <div className={`relative aspect-video bg-black rounded-2xl overflow-hidden ${className}`}>
        <iframe title={s.title} src={`https://www.youtube.com/embed/${yt[1]}`} className="absolute inset-0 w-full h-full" frameBorder="0" allowFullScreen data-testid="spotlight-embed" />
      </div>
    );
  }
  return (
    <div className={`relative aspect-video rounded-2xl overflow-hidden bg-[#111] border border-white/10 ${className}`}>
      {s.featured_image && <img src={fileUrl(s.featured_image)} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <Play size={40} className="text-rose-400" />
        {url && <a href={url} target="_blank" rel="noreferrer" className="bg-rose-500 hover:bg-rose-600 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="watch-external-btn">Watch on {s.video_platform || "the original platform"} <ExternalLink size={14} /></a>}
      </div>
    </div>
  );
}

export const LiveBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1.5 bg-red-600 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${className}`} data-testid="live-badge">
    <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live Now
  </span>
);

export function SpotlightCard({ s }) {
  return (
    <Link to={`/bar-dad/${s.slug}`} className="group block rounded-xl overflow-hidden bg-[#111] border border-white/10 card-hover" data-testid={`spotlight-card-${s.slug}`}>
      <div className="relative aspect-video overflow-hidden bg-black">
        {s.featured_image ? <img loading="lazy" src={thumbUrl(s.featured_image)} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><Clapperboard size={32} className="text-zinc-700" /></div>}
        {s.live_status === "live" ? <LiveBadge className="absolute top-3 left-3" />
          : <span className="absolute top-3 left-3 glass text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">{s.spotlight_type || "Spotlight"}</span>}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play size={36} className="text-white drop-shadow" /></div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold leading-tight group-hover:text-rose-400 transition-colors line-clamp-2">{s.title}</h3>
        <p className="text-xs text-zinc-500 mt-1.5">{(s.published_date || s.created_at || "").slice(0, 10)}{s.city ? ` · ${s.city}` : ""}</p>
      </div>
    </Link>
  );
}

export default function BarDad() {
  useSeo("Bar Dad Spotlight | Gulf Coast VIP", "Bar Dad live streams, venue visits, interviews and Gulf Coast nightlife coverage.");
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.get(`/bar-dad?page=${page}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/bar-dad/live-status").then((r) => setStatus(r.data)).catch(() => {});
  }, [page]);
  const live = status?.live;
  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="bardad-page">
      <p className="overline mb-2">Gulf Coast VIP Original</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-2 flex items-center gap-3"><Radio className="text-rose-400" size={36} /> Bar Dad Spotlight</h1>
      <p className="text-zinc-400 mb-8 max-w-2xl">Live streams, venue visits, interviews and nightlife coverage from across the coast.</p>
      {live && (
        <div className="mb-10" data-testid="bardad-live-hero">
          <div className="flex items-center gap-3 mb-3"><LiveBadge /><h2 className="font-display font-bold text-xl">{live.title}</h2></div>
          <SpotlightEmbed s={live} />
          {live.venue && <p className="text-sm text-zinc-400 mt-3 flex items-center gap-1.5"><MapPin size={13} /> Live from <Link to={`/venues/${live.venue.slug}`} className="text-rose-400 hover:underline">{live.venue.name}</Link></p>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {data.items.filter((s) => !live || s.id !== live.id).map((s) => <SpotlightCard key={s.id} s={s} />)}
      </div>
      {data.items.length === 0 && <p className="text-zinc-500 text-center py-16">No spotlights published yet — check back soon.</p>}
      <div className="mt-10"><Pagination page={page} pages={data.pages} onPage={setPage} /></div>
    </div>
  );
}

export function BarDadDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/bar-dad/${slug}`).then((r) => setS(r.data)).catch(() => setErr(true)); window.scrollTo(0, 0); }, [slug]);
  useSeo(s ? (s.seo_title || `${s.title} | Bar Dad Spotlight`) : "Bar Dad Spotlight", s?.seo_description || s?.short_description);
  if (err) return <div className="py-32 text-center text-zinc-500">Spotlight not found. <Link to="/bar-dad" className="text-rose-400">All spotlights</Link></div>;
  if (!s) return <div className="py-32 text-center text-zinc-600 animate-pulse">Loading…</div>;
  const rel = [["venue", "venues"], ["artist", "artists"], ["event", "events"], ["attraction", "attractions"], ["activity", "activities"]].filter(([k]) => s[k]);
  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 max-w-5xl mx-auto" data-testid="bardad-detail">
      <Link to="/bar-dad" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-5"><ArrowLeft size={14} /> All spotlights</Link>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {s.live_status === "live" ? <LiveBadge /> : <span className="glass text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full">{s.spotlight_type || "Spotlight"}</span>}
        <span className="text-xs text-zinc-500">{(s.published_date || s.created_at || "").slice(0, 10)}</span>
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mb-6">{s.title}</h1>
      <SpotlightEmbed s={s} className="mb-6" />
      {s.video_url && <a href={s.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-rose-400 mb-6" data-testid="bardad-original-link">Watch on Facebook <ExternalLink size={13} /></a>}
      {(s.description || s.short_description) && <Reveal><PipeText text={s.description || s.short_description} className="text-zinc-300 leading-relaxed mb-8" /></Reveal>}
      {rel.length > 0 && (
        <Reveal><SectionTitle kicker="Featured in this spotlight" title="Connected" />
          <div className="flex flex-wrap gap-3">
            {rel.map(([k, plural]) => (
              <Link key={k} to={`/${plural}/${s[k].slug}`} className="bg-[#111] border border-white/10 hover:border-rose-500/40 rounded-full px-4 py-2 text-sm" data-testid={`bardad-rel-${k}`}>
                <span className="text-zinc-500 capitalize">{k}:</span> <span className="font-bold">{s[k].name}</span>
              </Link>
            ))}
          </div></Reveal>
      )}
    </div>
  );
}
